import Link from "next/link";

import { Card, Skeleton, Switch } from "@chef/components";
import {
  ADDON_SUBSCRIPTIONS,
  BRAND_PATHS,
  FEATURE_FLAGS,
} from "@chef/constants";
import { useFlag } from "@chef/feature-flags";
import { Seedling } from "@chef/icons/large";
import {
  showNotification,
  useBillingQuery,
  useUpdateBillingAgreementAddonsMutation,
} from "@chef/state-management";
import { isEqualStrings } from "@chef/utils/equal";

import { intl } from "./DigitalRecipesToggle.Intl";

export const DigitalRecipesToggle = () => {
  const { enabled: isEnabled } = useFlag(FEATURE_FLAGS.DIGITAL_RECIPES);

  const { data: billingQuery, isLoading: isLoadingBilling } = useBillingQuery();

  const [updateBillingAddons, { isLoading: isMutating }] =
    useUpdateBillingAgreementAddonsMutation();

  const digitalRecipes = billingQuery?.billing.addonSubscriptions?.find((s) =>
    isEqualStrings(s.subscriptionId, ADDON_SUBSCRIPTIONS.DIGITAL_RECIPES),
  );
  const isOnlyPrintedRecipes = !digitalRecipes?.active;

  const handleToggleDigitalRecipes = async () => {
    if (!billingQuery) {
      return;
    }
    try {
      const addonSubscriptions = billingQuery.billing.addonSubscriptions;

      const updatedAddonSubscriptions = addonSubscriptions?.map((sub) => {
        if (
          isEqualStrings(
            sub.subscriptionId,
            ADDON_SUBSCRIPTIONS.DIGITAL_RECIPES,
          )
        ) {
          return {
            addonSubscriptionId: sub.subscriptionId,
            active: !sub.active,
          };
        }

        return { addonSubscriptionId: sub.subscriptionId, active: sub.active };
      });

      if (!digitalRecipes) {
        updatedAddonSubscriptions.push({
          addonSubscriptionId: ADDON_SUBSCRIPTIONS.DIGITAL_RECIPES,
          active: true,
        });
      }

      await updateBillingAddons(updatedAddonSubscriptions);
    } catch (e) {
      showNotification({
        type: "error",
        message: intl.GENERIC_ERROR_MESSAGE,
      });
      console.error(e);
    }
  };

  if (!isEnabled) {
    return null;
  }

  return (
    <section>
      <Card
        className="relative flex flex-col gap-2"
        id="abonnement-eco-choice-card"
      >
        <h2 className="text-xl">
          <strong>{intl.ACTIVE_ENVIREMENT_CHOICE}</strong>
        </h2>

        {isLoadingBilling && (
          <div className="flex flex-col gap-2 my-2">
            <Skeleton height={4} />
            <Skeleton height={4} width={80} />
          </div>
        )}

        {!isLoadingBilling && (
          <>
            <p className="mb-4 text-sm">
              {intl.YOU_WILL_FIND_DIGITAL_RECIPES_UNDER}
              <Link href={BRAND_PATHS.MY_RECIPES_PAGE} className="underline">
                {intl.MY_RECIPES_LABEL}
              </Link>
              {intl.AND_YOU_CAN_OPT_OUT_OF_PHYSICAL_RECIPES}
            </p>
            <hr className="mb-2" />
            <Seedling className="absolute text-2xl right-6" />
            <div className="flex gap-2">
              <Switch
                checked={isOnlyPrintedRecipes}
                onChange={handleToggleDigitalRecipes}
                className="mr-3 shrink-0"
                isLoading={isMutating}
                small
              />
              <p className="self-center">
                {isOnlyPrintedRecipes ? (
                  <strong>{intl.PRINTED_RECIPE_SHEETS_ARE_SENT_WITH}</strong>
                ) : (
                  <strong>{intl.THANX_YOU_TO_THINKING_ABOUT_ENVIREMENT}</strong>
                )}
              </p>
            </div>
          </>
        )}
      </Card>
    </section>
  );
};
